import { Behavior } from '@cewe-designsystem/abstract';
import { exitIntentDefaultSettings, ExitIntentSettings } from './exitIntentSettings';
import { IdleObserver } from './idleObserver';
import { CursorObserver } from './cursorObserver';
import { ExitIntentTriggerMethod, ExitIntentTriggerPayload } from './exitIntentTriggerPayload';
import UtilBrowserDetection from '@cewe-designsystem/util_browser-detection/dist';
import sessionStore from './sessionStore';
import localStore from './localStore';
import { ExitIntentStateProxy } from './exitIntentStateProxy';
import UtilBreakpoints from '@cewe-designsystem/util_breakpoints';

const MILLISECONDS_PER_DAY = 86400000;

@Behavior('exit-intent')
export class ExitIntent {
  /**
   * the host element the behavior is installed on.
   */
  element: Element;

  /**
   * settings for exit-intent.
   */
  settings: ExitIntentSettings = exitIntentDefaultSettings;

  /**
   * method to call on host element if the trigger is fired.
   */
  triggerMethod: string;

  /**
   * detect idle times of the user.
   */
  idleObserver: IdleObserver;

  /**
   * observes the cursor and detect mouse leave.
   */
  cursorObserver: CursorObserver;

  /**
   * state, only valid in a session.
   */
  state: ExitIntentStateProxy;

  /**
   * name of the event, that if caught at root element will set a trigger block.
   */
  watchedDisableEventName: string;

  /**
   *
   * @param element
   */
  constructor(element) {
    this.element = element;
  }

  /**
   * @inheritDoc
   */
  connectedCallback() {
    if (this.element.hasAttribute('exit-intent-settings')) {
      this.settings = { ...this.settings, ...JSON.parse(this.element.getAttribute('exit-intent-settings')) };
    }

    if (this.element.hasAttribute('exit-intent-trigger-callback')) {
      this.triggerMethod = this.element.getAttribute('exit-intent-trigger-callback');
    }

    if (this.element.hasAttribute('exit-intent-disable-event')) {
      this.watchedDisableEventName = this.element.getAttribute('exit-intent-disable-event');
    }

    // init session state
    this.state = new ExitIntentStateProxy(this.element.id, sessionStore, localStore);
    this.state.increaseVisit();

    // register disable detection
    this.watchDisableEvent();

    // register lazy if not blocked
    if (this.canTrigger()) {
      setTimeout(this.registerTrigger.bind(this), this.settings.trigger.detectionDelayMillis);
    }
  }

  /**
   * disables the action trigger for configured (session.trigger.disabledDays) time.
   */
  setInactive() {
    this.state.disableUntil = new Date(
      Date.now() + (this.settings.session.trigger.disabledDays ?? 1) * MILLISECONDS_PER_DAY,
    );
  }

  /**
   * calls the exit-intent trigger and calls the triggerMethod on the host element.
   */
  triggerExitIntent(method: ExitIntentTriggerMethod) {
    // increase triggers
    this.state.increaseTrigger();

    if (this.triggerMethod) {
      this.element[this.triggerMethod]?.call(this.element);
    }

    const data: ExitIntentTriggerPayload = {
      method,
      target: this.element,
      exitIntent: this,
    };
    this.element.dispatchEvent(new CustomEvent('on:exit-intent:trigger', { detail: data, bubbles: true }));
  }

  /**
   * @inheritDoc
   */
  disconnectedCallback() {
    this.idleObserver?.stop();
    this.cursorObserver?.stop();
  }

  /**
   * observes the host element to detect events that will disable the exit-intent trigger for the configured time.
   * @protected
   */
  watchDisableEvent() {
    if (this.watchedDisableEventName) {
      this.element.addEventListener(this.watchedDisableEventName, () => {
        this.setInactive();
      });
    }
  }

  /**
   * checks the conditions (visits, triggers, invalid until, mobile view) for the trigger to be called.
   */
  canTrigger(): boolean {
    return !this.isInactive() && !this.isBelowMinVisits() && !this.isMaxTriggersReached() && !this.isSmallDisplay();
  }

  /**
   * returns true if the max trigger limit is reached.
   */
  isMaxTriggersReached() {
    return (
      this.settings.session.trigger.maxTriggers !== null &&
      this.state.triggers >= this.settings.session.trigger.maxTriggers
    );
  }

  /**
   * returns true if the inactive Date has not passed.
   */
  isInactive() {
    return this.state.disableUntil !== undefined && new Date() < this.state.disableUntil;
  }

  /**
   * returns true if the min visits to trigger are not reached.
   */
  isBelowMinVisits() {
    return (
      this.settings.session.trigger.minPageVisits !== null &&
      this.state.visits < this.settings.session.trigger.minPageVisits
    );
  }

  /**
   * determine if the current viewport would show the newsletter popup in the fullscreen view
   * @private
   */
  private isSmallDisplay(): boolean {
    return new UtilBreakpoints().breakpointDown('sm');
  }

  /**
   * initialize the configured observer.
   */
  registerTrigger() {
    // register cursor observation
    if (this.settings.trigger.cursorObserver) {
      this.registerCursorObserver();
    }

    // register idle observation
    if (this.settings.trigger.idleObserver) {
      this.registerIdleObserver();
    }
  }

  /**
   * init the cursorObserver to observe mouse leaves.
   */
  registerCursorObserver() {
    this.cursorObserver = new CursorObserver({
      root: window,
      windowSize: { height: window.innerHeight, width: window.innerWidth },
      once: true,
      edge: {
        height: 50,
        width: Math.ceil(window.innerWidth / 3),
      },
      triggerDelayMillis: 400,
      detectOs: () => new UtilBrowserDetection().getOs(),
      callback: this.triggerExitIntent.bind(this, ExitIntentTriggerMethod.cursor),
      ...this.settings.trigger.cursorObserver, // override settings with given settings
    });
  }

  /**
   * init the idle observer to detect user idle times.
   */
  registerIdleObserver() {
    this.idleObserver = new IdleObserver({
      idleTimeMillis: 1000,
      once: true,
      ignoredEvents: [],
      root: window || document,
      callback: this.triggerExitIntent.bind(this, ExitIntentTriggerMethod.idle),
      ...this.settings.trigger.idleObserver, // override settings with given settings
    });
  }
}
